import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useStore } from "../../store";
import { templateCatalog } from "../../templates/templateCatalog";

const TemplateSelection = ({ onSelectTemplate }) => {
  const { selectedTemplate, loading } = useStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const templateGridItemProps = {
    xs: 12,
    sm: 6,
    md: 4,
  };
  const cardStyles = {
    aspectRatio: "3/4",
    borderRadius: 2,
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
    cursor: "pointer",
    backgroundColor: "#f8fafc",
  };
  const previewImageStyles = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "top center",
    backgroundColor: "#fff",
  };
  const existingTemplates = [
    { img: "Template1", name: "Template 1", color: "crimson" },
    { img: "Template2", name: "Template 2", color: "crimson"},
    { img: "Template3", name: "Recommended", color: "green" },
  ];

  const filteredTemplates = existingTemplates.filter(template => {
    if (isMobile) {
      return !template.hideOnMobile;
    } else {
      return !template.showOnlyMobile;
    }
  });

  return (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 500, color: "#444", mb: 3 }}>
        Select a Template
      </Typography>
      
      <Box sx={{ position: "relative", my: 4, maxWidth: "900px", mx: "auto" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(128, 128, 128, 0.3)",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
            }}
          >
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        )}
        
        <Grid container spacing={3} justifyContent="center" wrap="wrap">
          {filteredTemplates.map((template, index) => (
            <Grid item {...templateGridItemProps} key={template.img}>
              <Card
                sx={{
                  ...cardStyles,
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  },
                  transform: selectedTemplate === template.img ? "scale(1.02)" : "none",
                  boxShadow: selectedTemplate === template.img
                    ? "0 0 12px rgba(183, 174, 174, 0.6)"
                    : "none",
                }}
                onClick={() => onSelectTemplate(template.img)}
              >
                <CardMedia
                  component="img"
                  image={`/assets/temp${index + 1}.png`}
                  alt={template.name}
                  sx={previewImageStyles}
                />
              </Card>
              
              <Typography
                variant="h6"
                sx={{
                  color: template.name === "Recommended" ? "#fff" : template.color,
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  padding: "16px 30px",
                  marginTop: 2,
                  ...(template.name === "Recommended" && {
                    background: template.color,
                    borderRadius: 10,
                    boxShadow: `0 0 0 0 ${template.color}`,
                    "@keyframes glow": {
                      "0%": { boxShadow: `0 0 5px ${template.color}` },
                      "50%": { boxShadow: `0 0 15px ${template.color}` },
                      "100%": { boxShadow: `0 0 5px ${template.color}` },
                    },
                    animation: "glow 1.4s linear infinite",
                  }),
                }}
              >
                {template.name}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: "#444", mb: 1.5 }}
          >
            More Template Layouts
          </Typography>

          <Grid container spacing={3} justifyContent="center" wrap="wrap">
            {templateCatalog.map((template) => (
              <Grid item {...templateGridItemProps} key={template.id}>
                <Card
                  sx={{
                    ...cardStyles,
                    border: selectedTemplate === template.id
                      ? `2px solid ${template.palette.accent}`
                      : "1px solid rgba(15, 23, 42, 0.08)",
                    "&:hover": {
                      transform: "scale(1.03)",
                      boxShadow: "0 10px 24px rgba(0, 0, 0, 0.14)",
                    },
                  }}
                  onClick={() => onSelectTemplate(template.id)}
                >
                  <CardMedia
                    component="img"
                    image={`/assets/temp${template.number}.png`}
                    alt={template.name}
                    sx={previewImageStyles}
                  />
                </Card>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 1.5,
                    fontWeight: 700,
                    color: template.palette.primary,
                    textTransform: "uppercase",
                    fontSize: isMobile ? "0.9rem" : "0.95rem",
                  }}
                >
                  {template.name}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateSelection;
